export interface DeviceLocation {
  lat: number;
  lng: number;
  accuracy?: number;
  timestamp?: number;
}

export interface DeviceLocationRequestOptions {
  forceRefresh?: boolean;
  timeoutMs?: number;
  /** 사용자 클릭으로 시작된 요청일 때만 true. 자동 조회에서는 권한창을 띄우지 않는다. */
  interactive?: boolean;
}

export type DeviceLocationError =
  | "PERMISSION_REQUIRED"
  | "PERMISSION_DENIED"
  | "PERMISSION_PERMANENTLY_DENIED"
  | "BROWSER_PERMISSION_DENIED"
  | "LOCATION_SERVICES_DISABLED"
  | "TIMEOUT"
  | "UNAVAILABLE";

export type DeviceLocationResult =
  | { location: DeviceLocation; error: null }
  | { location: null; error: DeviceLocationError };

interface NativeLocationPayload {
  success?: boolean;
  error?: string;
  latitude?: number;
  longitude?: number;
  lat?: number;
  lng?: number;
  accuracy?: number;
  timestamp?: number;
}

declare global {
  interface Window {
    __mungLocationResult?: (
      requestId: string,
      payload: NativeLocationPayload
    ) => void;
  }
}

const pending = new Map<
  string,
  {
    resolve: (result: DeviceLocationResult) => void;
    timeoutId: number;
  }
>();

const LOCATION_CACHE_MAX_AGE_MS = 5 * 60 * 1000;
const DEFAULT_NATIVE_TIMEOUT_MS = 30_000;
const DEFAULT_BROWSER_TIMEOUT_MS = 7_000;

let callbackInstalled = false;
let cachedLocation: DeviceLocation | null = null;
let cachedLocationAt = 0;
let inFlightLocation: {
  promise: Promise<DeviceLocationResult>;
  forceRefresh: boolean;
  interactive: boolean;
} | null = null;

const failure = (error: DeviceLocationError): DeviceLocationResult => ({
  location: null,
  error,
});

const success = (location: DeviceLocation): DeviceLocationResult => ({
  location,
  error: null,
});

const isDeviceLocationError = (value: unknown): value is DeviceLocationError =>
  typeof value === "string" &&
  [
    "PERMISSION_REQUIRED",
    "PERMISSION_DENIED",
    "PERMISSION_PERMANENTLY_DENIED",
    "BROWSER_PERMISSION_DENIED",
    "LOCATION_SERVICES_DISABLED",
    "TIMEOUT",
    "UNAVAILABLE",
  ].includes(value);

const makeRequestId = () =>
  `loc_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const toDeviceLocationResult = (
  payload: NativeLocationPayload
): DeviceLocationResult => {
  if (payload.success === false) {
    return failure(
      isDeviceLocationError(payload.error) ? payload.error : "UNAVAILABLE"
    );
  }

  const lat = payload.latitude ?? payload.lat;
  const lng = payload.longitude ?? payload.lng;
  if (
    typeof lat !== "number" ||
    !Number.isFinite(lat) ||
    typeof lng !== "number" ||
    !Number.isFinite(lng)
  ) {
    return failure("UNAVAILABLE");
  }

  return success({
    lat,
    lng,
    accuracy:
      typeof payload.accuracy === "number" ? payload.accuracy : undefined,
    timestamp:
      typeof payload.timestamp === "number" ? payload.timestamp : Date.now(),
  });
};

const getFreshCachedLocation = () => {
  if (!cachedLocation) return null;
  if (Date.now() - cachedLocationAt > LOCATION_CACHE_MAX_AGE_MS) return null;
  return cachedLocation;
};

const rememberLocation = (result: DeviceLocationResult) => {
  if (result.location) {
    cachedLocation = result.location;
    cachedLocationAt = Date.now();
  }
  return result;
};

const installNativeCallback = () => {
  if (callbackInstalled || typeof window === "undefined") return;

  const previous = window.__mungLocationResult;
  window.__mungLocationResult = (requestId, payload) => {
    const item = pending.get(requestId);
    if (!item) {
      previous?.(requestId, payload);
      return;
    }

    window.clearTimeout(item.timeoutId);
    pending.delete(requestId);
    item.resolve(toDeviceLocationResult(payload));
  };

  callbackInstalled = true;
};

const requestFromNative = (
  send: (requestId: string) => void,
  timeoutMs: number
): Promise<DeviceLocationResult> => {
  installNativeCallback();
  const requestId = makeRequestId();

  return new Promise((resolve) => {
    const timeoutId = window.setTimeout(() => {
      pending.delete(requestId);
      resolve(failure("TIMEOUT"));
    }, timeoutMs);

    pending.set(requestId, { resolve, timeoutId });

    try {
      send(requestId);
    } catch {
      window.clearTimeout(timeoutId);
      pending.delete(requestId);
      resolve(failure("UNAVAILABLE"));
    }
  });
};

const requestFromBrowser = (
  timeoutMs: number,
  forceRefresh: boolean,
  interactive: boolean
): Promise<DeviceLocationResult> => {
  if (!navigator.geolocation) {
    return Promise.resolve(failure("UNAVAILABLE"));
  }

  const requestPosition = () =>
    new Promise<DeviceLocationResult>((resolve) => {
      let settled = false;
      const finish = (result: DeviceLocationResult) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        resolve(result);
      };
      const timeoutId = window.setTimeout(() => {
        finish(failure("TIMEOUT"));
      }, timeoutMs + 500);

      navigator.geolocation.getCurrentPosition(
        (pos) =>
          finish(
            success({
              lat: pos.coords.latitude,
              lng: pos.coords.longitude,
              accuracy: pos.coords.accuracy,
              timestamp: pos.timestamp,
            })
          ),
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            if (!navigator.permissions?.query) {
              finish(failure("BROWSER_PERMISSION_DENIED"));
              return;
            }
            navigator.permissions
              .query({ name: "geolocation" })
              .then((permission) =>
                finish(
                  failure(
                    permission.state === "denied"
                      ? "BROWSER_PERMISSION_DENIED"
                      : "PERMISSION_DENIED"
                  )
                )
              )
              .catch(() => finish(failure("BROWSER_PERMISSION_DENIED")));
          } else if (error.code === error.TIMEOUT) {
            finish(failure("TIMEOUT"));
          } else {
            finish(failure("UNAVAILABLE"));
          }
        },
        {
          enableHighAccuracy: true,
          timeout: timeoutMs,
          maximumAge: forceRefresh ? 0 : LOCATION_CACHE_MAX_AGE_MS,
        }
      );
    });

  if (interactive) return requestPosition();

  // 자동 진입 시 브라우저 권한창이 뜨지 않도록, 이미 허용된 경우에만 조회한다.
  if (!navigator.permissions?.query) {
    return Promise.resolve(failure("PERMISSION_REQUIRED"));
  }

  return navigator.permissions
    .query({ name: "geolocation" })
    .then((permission) => {
      if (permission.state === "granted") return requestPosition();
      return failure(
        permission.state === "denied"
          ? "BROWSER_PERMISSION_DENIED"
          : "PERMISSION_REQUIRED"
      );
    })
    .catch(() => failure("PERMISSION_REQUIRED"));
};

export const requestDeviceLocation = (
  options: DeviceLocationRequestOptions = {}
): Promise<DeviceLocationResult> => {
  if (typeof window === "undefined") {
    return Promise.resolve(failure("UNAVAILABLE"));
  }

  const forceRefresh = options.forceRefresh === true;
  const interactive = options.interactive === true;

  if (!forceRefresh) {
    const cached = getFreshCachedLocation();
    if (cached) return Promise.resolve(success(cached));
  }

  if (inFlightLocation) {
    const satisfiesFreshness = !forceRefresh || inFlightLocation.forceRefresh;
    const satisfiesInteraction = !interactive || inFlightLocation.interactive;
    if (satisfiesFreshness && satisfiesInteraction) {
      return inFlightLocation.promise;
    }
  }

  const w = window as any;
  const iosLocationBridge = w.webkit?.messageHandlers?.mungLocation;
  const legacyIosBridge = w.webkit?.messageHandlers?.mung;
  const androidBridge = w.mung;
  let requestPromise: Promise<DeviceLocationResult>;

  if (typeof iosLocationBridge?.postMessage === "function") {
    requestPromise = requestFromNative(
      (requestId) =>
        iosLocationBridge.postMessage([
          "requestCurrentLocation",
          requestId,
          forceRefresh || interactive,
        ]),
      options.timeoutMs ?? DEFAULT_NATIVE_TIMEOUT_MS
    );
  } else if (
    typeof androidBridge?.requestCurrentLocation === "function" &&
    (interactive || typeof androidBridge?.openLocationSettings === "function")
  ) {
    requestPromise = requestFromNative(
      (requestId) =>
        androidBridge.requestCurrentLocation(
          requestId,
          forceRefresh || interactive
        ),
      options.timeoutMs ?? DEFAULT_NATIVE_TIMEOUT_MS
    );
  } else if (
    interactive &&
    typeof legacyIosBridge?.postMessage === "function"
  ) {
    // 구버전 iOS 앱에서도 사용자가 직접 누른 요청은 기존 브리지로 동작한다.
    requestPromise = requestFromNative(
      (requestId) =>
        legacyIosBridge.postMessage([
          "requestCurrentLocation",
          requestId,
          true,
        ]),
      options.timeoutMs ?? DEFAULT_NATIVE_TIMEOUT_MS
    );
  } else if (
    typeof legacyIosBridge?.postMessage === "function" ||
    typeof androidBridge?.requestCurrentLocation === "function"
  ) {
    // 구버전 앱의 자동 요청이 OS 권한창을 띄우지 않도록 조용히 종료한다.
    requestPromise = Promise.resolve(failure("PERMISSION_REQUIRED"));
  } else {
    requestPromise = requestFromBrowser(
      options.timeoutMs ?? DEFAULT_BROWSER_TIMEOUT_MS,
      forceRefresh,
      interactive
    );
  }

  let trackedPromise: Promise<DeviceLocationResult>;
  trackedPromise = requestPromise
    .then(rememberLocation)
    .finally(() => {
      if (inFlightLocation?.promise === trackedPromise) {
        inFlightLocation = null;
      }
    });

  inFlightLocation = { promise: trackedPromise, forceRefresh, interactive };
  return trackedPromise;
};

export const openNativeLocationSettings = (
  scope: "app" | "device" = "app"
): boolean => {
  if (typeof window === "undefined") return false;

  const w = window as any;
  const iosBridge =
    w.webkit?.messageHandlers?.mungLocation ?? w.webkit?.messageHandlers?.mung;
  if (typeof iosBridge?.postMessage === "function") {
    iosBridge.postMessage(["openLocationSettings", scope]);
    return true;
  }

  if (typeof w.mung?.openLocationSettings === "function") {
    w.mung.openLocationSettings(scope);
    return true;
  }

  return false;
};
